import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { sortByTitle } from "@/lib/sortRows";
import { roleLabel } from "@/lib/roleLabels";
import AppLayout from "@/components/AppLayout";
import { STATUS_STYLES } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import DataTable from "@/components/DataTable";
import { StatusPill } from "@/components/StatusBadge";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area, Legend,
} from "recharts";
import { ArrowLeftIcon, SparkleIcon, GraduationCapIcon, XIcon, CheckCircleIcon, CircleIcon, CircleDashedIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

// Feeds off /stats/member/:id's task status_distribution (tasks.status —
// open/wip/test/uat/cutover/live/hold). Unrelated to the onboarding-path
// item statuses (not_started/in_progress/done) used further down this
// file — those live in a separate JSONB field, not the tasks table.
const PIE_COLORS = { open: "#94A3B8", wip: "#0176D3", test: "#7C3AED", uat: "#0891B2", cutover: "#EA580C", live: "#059669", closed: "#047857", cancelled: "#B45309", hold: "#D97706" };

export default function TeamMemberDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [growth, setGrowth] = useState(null); // {events, timeline}
  const [onboarding, setOnboarding] = useState(null);
  const [onbLoading, setOnbLoading] = useState(false);

  const load = async () => {
    const [u, s, t, p, g, o] = await Promise.all([
      api.get(`/users/${id}`),
      api.get(`/stats/member/${id}`),
      api.get(`/tasks?assignee_id=${id}`),
      api.get(`/projects`),
      api.get(`/users/${id}/skill-history`),
      api.get(`/onboarding/skill-path/${id}`).catch(() => ({ data: null })),
    ]);
    setUser(u.data);
    setStats(s.data);
    setTasks(t.data);
    setProjects(sortByTitle(p.data));
    setGrowth(g.data);
    setOnboarding(o.data);
  };
  useEffect(() => { load(); }, [id]);

  const generateReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/reports/generate", { scope: "member", target_id: id });
      toast.success("Report generated");
      nav(`/reports?highlight=${data._id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const generateOnboarding = async () => {
    setOnbLoading(true);
    try {
      const { data } = await api.post(`/onboarding/skill-path/${id}`);
      setOnboarding(data);
      toast.success("Onboarding path generated");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setOnbLoading(false); }
  };

  const cycleItemStatus = async (idx) => {
    const cur = onboarding?.path?.[idx]?.status || "not_started";
    const next = cur === "not_started" ? "in_progress" : cur === "in_progress" ? "done" : "not_started";
    try {
      const { data } = await api.patch(`/onboarding/skill-path/${id}/items/${idx}`, { status: next });
      setOnboarding(data);
      if (next === "done") toast.success("Nice — item complete");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const deleteItem = async (idx) => {
    if (!confirm("Remove this item from the onboarding path?")) return;
    try {
      const { data } = await api.delete(`/onboarding/skill-path/${id}/items/${idx}`);
      setOnboarding(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  // Pagination now lives inside <DataTable>, which sidesteps the
  // Rules-of-Hooks trap this page used to have to work around: the hook can't
  // be skipped on the loading render any more, because it belongs to a child
  // component that simply isn't mounted yet rather than to this one.

  if (!user || !stats) return <AppLayout><div className="p-10 text-sm text-zinc-500">Loading...</div></AppLayout>;

  const pMap = Object.fromEntries(projects.map((p) => [p._id, p.title]));

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4">
        <Link to="/team" className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-md border border-[#0176D3] bg-[#EFF6FF] text-sm font-semibold text-[#0176D3] hover:bg-[#0176D3] hover:text-white transition-colors">
          <ArrowLeftIcon size={18} weight="bold" /> Back to Team
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="trk-uppercase-label">{roleLabel(user.role)}</div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">{user.name}</h1>
            <div className="text-sm text-zinc-500">{user.email}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={generateOnboarding} disabled={onbLoading} data-testid="generate-onboarding" variant="outline" className="rounded-sm border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white">
              <GraduationCapIcon size={14} className="mr-1" weight="fill" /> {onbLoading ? "Building path…" : (onboarding ? "Regenerate Path" : "Onboarding Path")}
            </Button>
            <Button onClick={generateReport} disabled={loading} data-testid="generate-member-report" className="rounded-sm bg-[#0A0A0A] hover:bg-black text-white">
              <SparkleIcon size={14} className="mr-1" weight="fill" /> {loading ? "Generating..." : "AI Progress Report"}
            </Button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-6 trk-fade-in">
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 border border-border rounded-sm overflow-hidden">
          {stats.status_distribution.map((s, i) => (
            <div key={s.status} className={`p-5 bg-white ${i < stats.status_distribution.length - 1 ? "border-r border-border" : ""}`}>
              <div className="text-xs font-mono tracking-wider text-zinc-500">{STATUS_STYLES[s.status]?.label}</div>
              <div className="font-display font-extrabold text-3xl mt-2 tabular-nums">{s.count}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-border rounded-sm p-5">
            <div className="trk-uppercase-label">Completion trend</div>
            <h3 className="font-display text-lg font-bold mb-4">Last 30 days</h3>
            <div className="h-56">
              <ResponsiveContainer debounce={250}>
                <LineChart data={stats.completion_series}>
                  <CartesianGrid stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717A" }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: "#71717A" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 4, fontSize: 12 }} />
                  <Line type="monotone" dataKey="completed" stroke="#0176D3" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="border border-border rounded-sm p-5">
            <div className="trk-uppercase-label">Distribution</div>
            <h3 className="font-display text-lg font-bold mb-4">By status</h3>
            <div className="h-44">
              <ResponsiveContainer debounce={250}>
                <PieChart>
                  <Pie data={stats.status_distribution} dataKey="count" nameKey="status" innerRadius={35} outerRadius={65}>
                    {stats.status_distribution.map((e) => <Cell key={e.status} fill={PIE_COLORS[e.status]} stroke="none" />)}
                  </Pie>
                  <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 4, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Onboarding Skill Path */}
        {onboarding && (
          <div className="border border-border rounded-sm p-5 bg-[#0A0A0A] text-white" data-testid="onboarding-path">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <GraduationCapIcon size={16} weight="fill" className="text-emerald-400" />
                <span className="trk-uppercase-label text-white/60">Personalised Onboarding Path</span>
                <span className="text-[10px] font-mono text-white/40">· Claude Sonnet 4.5</span>
              </div>
              <button onClick={() => setOnboarding(null)} className="text-white/60 hover:text-white" data-testid="dismiss-onboarding">
                <XIcon size={14} />
              </button>
            </div>
            {onboarding.welcome && (
              <p className="text-sm text-white/90 mb-3 leading-relaxed max-w-3xl">{onboarding.welcome}</p>
            )}

            {/* Progress bar */}
            {(() => {
              const total = (onboarding.path || []).length;
              const done = (onboarding.path || []).filter((p) => p.status === "done").length;
              const prog = (onboarding.path || []).filter((p) => p.status === "in_progress").length;
              const pct = total ? Math.round((done / total) * 100) : 0;
              return (
                <div className="mb-4" data-testid="onboarding-progress">
                  <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-white/60 mb-1.5">
                    <span>Progress</span>
                    <span className="text-white">{done}/{total} · {pct}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center gap-3 text-[10px] font-mono text-white/50 mt-1.5">
                    <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />{done} done</span>
                    <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />{prog} in progress</span>
                    <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white/30" />{total - done - prog} not started</span>
                  </div>
                </div>
              );
            })()}

            <div className="grid md:grid-cols-3 gap-3">
              {(onboarding.path || []).map((step, i) => {
                const status = step.status || "not_started";
                const cardCls = status === "done"
                  ? "border-emerald-500/40 bg-emerald-500/10"
                  : status === "in_progress"
                    ? "border-amber-400/40 bg-amber-400/5"
                    : "border-white/15 bg-white/[0.03]";
                const StatusIcon = status === "done" ? CheckCircleIcon : status === "in_progress" ? CircleDashedIcon : CircleIcon;
                return (
                  <div key={i} data-testid={`onb-step-${i}`} className={`border rounded-sm p-3 transition-colors ${cardCls}`}>
                    <div className="flex items-center justify-between mb-1">
                      <button
                        onClick={() => cycleItemStatus(i)}
                        data-testid={`onb-status-toggle-${i}`}
                        className="inline-flex items-center gap-1.5 hover:opacity-80"
                        title="Click to cycle status"
                      >
                        <StatusIcon size={14} weight={status === "done" ? "fill" : "bold"} className={status === "done" ? "text-emerald-400" : status === "in_progress" ? "text-amber-400" : "text-white/50"} />
                        <span className="text-[10px] font-mono uppercase tracking-wider text-white/70">{status.replace("_", " ")}</span>
                      </button>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">{step.phase}</span>
                        <button
                          onClick={() => deleteItem(i)}
                          data-testid={`onb-delete-${i}`}
                          className="text-white/40 hover:text-red-400"
                          title="Remove"
                        >
                          <XIcon size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className={`font-display font-bold text-base tracking-tight ${status === "done" ? "line-through text-white/60" : ""}`}>
                        {step.skill}
                      </div>
                      <span className="text-[10px] font-mono uppercase tracking-wider text-white/50">→ {step.target_level}</span>
                    </div>
                    {step.why && <p className="text-xs text-white/70 mt-1 leading-relaxed">{step.why}</p>}
                    {step.how && (
                      <div className="text-[10px] font-mono text-white/50 mt-2 border-l border-white/20 pl-2 italic">
                        First task: {step.how}
                      </div>
                    )}
                    {(step.resources || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {step.resources.map((r, ri) => (
                          <a key={ri} href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-sm bg-white/10 hover:bg-white/20 text-white/90 border border-white/10">
                            <span className="w-1 h-1 rounded-full bg-emerald-400" />
                            {r.title}
                          </a>
                        ))}
                      </div>
                    )}
                    {step.completed_at && (
                      <div className="text-[10px] font-mono text-emerald-400/80 mt-2">✓ Completed {new Date(step.completed_at).toLocaleDateString()}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Skill Growth */}
        {growth && growth.timeline && growth.timeline.length > 0 && (
          <div className="border border-border rounded-sm p-5" data-testid="skill-growth-chart">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="trk-uppercase-label">Skill growth</div>
                <h3 className="font-display text-lg font-bold">Level progression over time</h3>
              </div>
              <div className="text-xs font-mono text-zinc-500">{growth.events.length} events</div>
            </div>
            <div className="h-64">
              <ResponsiveContainer debounce={250}>
                <AreaChart data={growth.timeline.map((t) => ({
                  at: new Date(t.at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
                  beginner: t.beginner,
                  moderate: t.moderate,
                  proficient: t.proficient,
                }))}>
                  <defs>
                    <linearGradient id="gBeg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#71717A" stopOpacity={0.35} /><stop offset="100%" stopColor="#71717A" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gMod" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.35} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gPro" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="at" tick={{ fontSize: 10, fill: "#71717A" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#71717A" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 4, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="stepAfter" dataKey="proficient" name="Proficient" stroke="#10B981" strokeWidth={2} fill="url(#gPro)" />
                  <Area type="stepAfter" dataKey="moderate"   name="Moderate"   stroke="#F59E0B" strokeWidth={2} fill="url(#gMod)" />
                  <Area type="stepAfter" dataKey="beginner"   name="Beginner"   stroke="#71717A" strokeWidth={2} fill="url(#gBeg)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-1.5 max-h-32 overflow-y-auto">
              {growth.events.slice().reverse().slice(0, 8).map((ev, i) => {
                const act = ev.action;
                const dot = act === "upgraded" ? "#10B981" : act === "added" ? "#0176D3" : act === "downgraded" ? "#F59E0B" : "#E3342F";
                return (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dot }} />
                    <span className="font-mono text-zinc-400 w-32">{new Date(ev.at).toLocaleDateString()}</span>
                    <span className="font-medium">{ev.skill}</span>
                    <span className="text-zinc-500">
                      {act}{ev.previous_level ? ` (${ev.previous_level} → ${ev.level})` : ev.level ? ` (${ev.level})` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DataTable
          title="Assigned Tasks"
          storageKey="teamMemberTasks.visibleColumns"
          testIdPrefix="team-member-tasks"
          rows={tasks}
          rowKey={(t) => t._id}
          pageSize={20}
          emptyMessage="No tasks assigned."
          columns={[
            {
              key: "task", label: "Task", locked: true, cls: "px-4",
              cell: (t) => <div className="break-words font-medium" title={t.title}>{t.title}</div>,
            },
            {
              key: "project", label: "Project", cls: "px-4",
              cell: (t) => (
                <Link
                  to={`/projects/${t.project_id}`}
                  className="block break-words text-zinc-600 hover:text-[#0176D3]"
                  title={pMap[t.project_id] || ""}
                >
                  {pMap[t.project_id] || "—"}
                </Link>
              ),
            },
            {
              key: "status", label: "Status", cls: "px-3", weight: 128,
              cell: (t) => <StatusPill status={t.status} />,
            },
            {
              key: "priority", label: "Priority", cls: "px-3", weight: 104,
              cell: (t) => <span className="font-mono text-xs uppercase text-zinc-600">{t.priority}</span>,
            },
          ]}
        />
      </div>
    </AppLayout>
  );
}
